/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios'
import { REFRESH_CSRF_COOKIE_NAME } from '@/api/refreshCsrf'

type AxiosLikeError = {
  config?: { skipAuthRefresh?: boolean; _retry?: boolean }
  response?: { status?: number; data?: any }
}

/** Typed error for server errors (5xx) so callers can distinguish them from network errors. */
export class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

function toApiError(err: unknown): ApiError | null {
  const e = err as AxiosLikeError
  const status = e.response?.status
  if (status && status >= 500) {
    const msg = e.response?.data?.message || 'Server error. Please try again later.'
    return new ApiError(msg, status)
  }
  return null
}

const ax = axios as any

const api = ax.create({
  baseURL: import.meta.env.VITE_API_BASE || '/api/v1',
  withCredentials: true,
  xsrfCookieName: 'csrf_access_token',
  xsrfHeaderName: 'X-CSRF-TOKEN',
}) as {
  (config: any): Promise<any>
  get: <T = unknown>(url: string, config?: any) => Promise<{ data: T }>
  post: <T = unknown>(url: string, data?: any, config?: any) => Promise<{ data: T }>
  patch: <T = unknown>(url: string, data?: any, config?: any) => Promise<{ data: T }>
  delete: <T = unknown>(url: string, config?: any) => Promise<{ data: T }>
  interceptors: {
    response: { use: (ok: (r: any) => any, err: (e: unknown) => any) => void }
  }
}

api.interceptors.response.use(
  (r: any) => r,
  async (err: unknown) => {
    const e = err as AxiosLikeError
    if (!e.config) {
      return Promise.reject(err)
    }
    const cfg = e.config
    if (cfg.skipAuthRefresh) {
      return Promise.reject(toApiError(err) || err)
    }
    if (e.response?.status !== 401) {
      return Promise.reject(toApiError(err) || err)
    }
    if (cfg._retry) {
      return Promise.reject(toApiError(err) || err)
    }
    cfg._retry = true
    try {
      await api.post(
        '/public/auth/refresh',
        {},
        { skipAuthRefresh: true, xsrfCookieName: REFRESH_CSRF_COOKIE_NAME },
      )
      return api(cfg)
    } catch {
      return Promise.reject(toApiError(err) || err)
    }
  },
)

export default api
